import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getProgrammes, setSelectedProgramme, getSelectedProgramme, logoutUser } from "../../service/authService";
import { toast } from "react-toastify";
import Logo from "../../assets/images/logo.png";
import { useAppContext } from "../../functions/ContextAPI";

const SettingsPage = () => {
  const navigate = useNavigate();
  const context = useAppContext();
  const [programmes, setProgrammes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load programmes from localStorage
    const storedProgrammes = getProgrammes();
    if (storedProgrammes && storedProgrammes.length > 0) {
      setProgrammes(storedProgrammes);

      // Load currently selected programme
      const currentlySelected = getSelectedProgramme();
      if (currentlySelected) {
        const selectedProgrammeId = currentlySelected._id || currentlySelected.id;
        setSelectedId(selectedProgrammeId);
      }
    } else {
      toast.warning("No programmes found. Please login again.");
      setTimeout(() => navigate("/login"), 2000);
    }
  }, [navigate]);

  const handleSelectProgramme = (programmeId, programme) => {
    // Check if programme is available (stage >= 5)
    if (!isProgrammeAvailable(programme)) {
      toast.warning("This programme is not ready for panel access yet");
      return;
    }
    setSelectedId(programmeId);
  };

  const getStageLabel = (stage) => {
    const stages = {
      0: "Has not started",
      1: "NUC sent to DAP",
      2: "DAP sent to HOD",
      3: "HOD sent to DAP",
      4: "DAP sent to NUC",
      5: "NUC sent to Panel",
      6: "Panel return to NUC",
      10: "Accreditation Completed"
    };
    return stages[stage] || "Unknown Stage";
  };

  const getStageColor = (stage) => {
    switch(stage) {
      case 1: return 'bg-blue-100 text-blue-800';
      case 2: return 'bg-yellow-100 text-yellow-800';
      case 3: return 'bg-orange-100 text-orange-800';
      case 4: return 'bg-purple-100 text-purple-800';
      case 5: return 'bg-green-100 text-green-800';
      case 6: return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isProgrammeAvailable = (programme) => {
    const stage = programme?.accreditationData?.stage || 0;
    return stage >= 5;
  };

  const handleContinue = async () => {
    if (!selectedId) {
      toast.error("Please select a programme to continue");
      return;
    }

    try {
      setLoading(true);

      // Find selected programme
      const selected = programmes.find(p => p._id === selectedId || p.id === selectedId);

      if (!selected) {
        toast.error("Selected programme not found");
        return;
      }

      // Save selected programme to localStorage
      const saved = setSelectedProgramme(selected);

      if (saved) {
        // Also update context immediately
        context.setSelectedProgramme(selected);

        toast.success("Programme selected successfully");
        setTimeout(() => {
          navigate("/dashboard");
        }, 500);
      } else {
        toast.error("Failed to save programme selection");
      }
    } catch (error) {
      console.error("Error selecting programme:", error);
      toast.error("An error occurred while selecting programme");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      toast.success("Logged out successfully");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src={Logo} alt="NUC Logo" className="h-12 w-auto" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  NUC Accreditation Management
                </h1>
                <p className="text-sm text-gray-600">Representative Portal</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Select Programme
          </h2>
          <p className="text-gray-600">
            Choose the programme you want to work on. You can change this selection later from the dashboard.
          </p>
        </div>

        {/* Programme Grid */}
        {programmes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {programmes.map((programme) => {
              const programmeId = programme._id || programme.id;
              const isSelected = selectedId === programmeId;
              const isAvailable = isProgrammeAvailable(programme);
              const stage = programme?.accreditationData?.stage || 0;

              return (
                <div
                  key={programmeId}
                  onClick={() => handleSelectProgramme(programmeId, programme)}
                  className={`
                    relative bg-white rounded-xl shadow-md border-2
                    transition-all duration-200
                    ${!isAvailable
                      ? "opacity-60 cursor-not-allowed"
                      : "cursor-pointer hover:shadow-lg"
                    }
                    ${isSelected
                      ? "border-green-500 ring-2 ring-green-200 bg-green-50"
                      : "border-gray-200 hover:border-primary-300"
                    }
                  `}
                >
                  {/* Not Ready Badge for stage < 5 */}
                  {!isAvailable && (
                    <div className="absolute top-4 right-4 z-10">
                      <div className="flex items-center space-x-1 bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span>Not Ready</span>
                      </div>
                    </div>
                  )}

                  {/* Selection Indicator */}
                  {isSelected && isAvailable && (
                    <div className="absolute top-4 right-4">
                      <div className="bg-green-500 text-white rounded-full p-1">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}

                  <div className="p-6">
                    {/* Programme Name */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 pr-8">
                      {programme.programmeName || programme.name || "Unnamed Programme"}
                    </h3>

                    {/* Programme Details */}
                    <div className="space-y-2">
                      {/* Stage Information */}
                      <div className="mb-3 pb-3 border-b border-gray-200">
                        <div className="text-xs text-gray-500 mb-1">Current Stage:</div>
                        <div className="flex items-start space-x-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStageColor(stage)}`}>
                            Stage {stage}
                          </span>
                          <span className="text-xs text-gray-600 leading-5 flex-1">
                            {getStageLabel(stage)}
                          </span>
                        </div>
                      </div>

                      {programme.institutionName && (
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span className="text-sm text-gray-700">
                            {programme.institutionName}
                          </span>
                        </div>
                      )}

                      {programme.facultyName && (
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                          <span className="text-sm text-gray-700">
                            {programme.facultyName}
                          </span>
                        </div>
                      )}

                      {programme.departmentName && (
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span className="text-sm text-gray-700">
                            {programme.departmentName}
                          </span>
                        </div>
                      )}

                      {programme.programmeType && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            {programme.programmeType}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Radio Button Indicator */}
                  <div className="absolute bottom-4 right-4">
                    <div className={`
                      w-6 h-6 rounded-full border-2 flex items-center justify-center
                      ${isSelected
                        ? "border-green-500 bg-green-500"
                        : "border-gray-300 bg-white"
                      }
                    `}>
                      {isSelected && (
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No programmes available</h3>
            <p className="mt-1 text-sm text-gray-500">
              Please contact NUC if you believe this is an error.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        {programmes.length > 0 && (
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleContinue}
              disabled={!selectedId || loading}
              className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading && (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <span className="text-white">Continue to Dashboard</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-xs text-gray-500">
            <span className="font-medium">&copy;{new Date().getFullYear()}</span>
            <span className="font-medium">
              POWERED BY <span className="text-primary-600 font-semibold">smartsourcing.ng</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;